import { FunctionComponent, PropsWithChildren, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from './ErrorPage';
import { useAnalytics } from '../Analytics/AnalyticsProvider';
import { FailureReasonType } from '../../clients/analytics-client';
import {
  ExpiredContextTokenError,
  HttpError,
  InvalidContextToken,
  isError,
} from '../../definitions/errors';

const JWT_VALIDATION_ERRORS = [ExpiredContextTokenError, InvalidContextToken];

const Boundary: FunctionComponent<PropsWithChildren<{}>> = ({ children }) => {
  const analytics = useAnalytics();

  const errorHandler = useCallback(
    (error: any) => {
      const failure = createFailureEvent(error);
      if (failure) {
        analytics.sendErrorEvent(failure);
      }
    },
    [analytics],
  );

  return (
    <ErrorBoundary
      fallbackRender={(props) => <ErrorPage {...props} />}
      onError={errorHandler}
    >
      {children}
    </ErrorBoundary>
  );
};

function createFailureEvent(error: Object): FailureReasonType {
  if (isError(error)) {
    switch (error.error) {
      case 'unexpected':
        // Handle jwt validation errors from the consent token
        if (JWT_VALIDATION_ERRORS.includes(error)) {
          return 'jwtValidationError';
        }
        break;
      case 'unauthorized-scopes':
      case 'no-scopes':
        return 'infoInputValidationError';
      // Unused errors
      case 'no-access':
      case 'no-sites':
        break;
      default:
        // Ensures all Errors variants are covered
        return error satisfies never;
    }
  }
  // Handle unexpected http errors
  if (error instanceof HttpError) {
    if (400 <= error.status && error.status < 500) {
      return 'remote4xxError';
    }
    if (500 <= error.status && error.status < 600) {
      return 'remote5xxError';
    }
  }
  return 'other';
}

export default Boundary;
